import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Eye, Search, Key, AlertTriangle, Package, Copy, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function AdminCodes() {
  const [products, setProducts] = useState([]);
  const [statsByProduct, setStatsByProduct] = useState({});
  const [search, setSearch] = useState("");
  const [activeProduct, setActiveProduct] = useState(null);
  const [lowStock, setLowStock] = useState([]);

  const reloadAll = async () => {
    try {
      const [pr, ls] = await Promise.all([
        api.get("/products?limit=500"),
        api.get("/admin/codes/low-stock?threshold=5"),
      ]);
      setProducts(pr.data.items || []);
      setLowStock(ls.data.items || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pr, ls] = await Promise.all([
          api.get("/products?limit=500"),
          api.get("/admin/codes/low-stock?threshold=5"),
        ]);
        if (mounted) {
          setProducts(pr.data.items || []);
          setLowStock(ls.data.items || []);
        }
      } catch (e) { console.error(e); }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => products.filter((p) => p.title.toLowerCase().includes(search.toLowerCase())), [products, search]);

  const lowMap = useMemo(() => Object.fromEntries(lowStock.map((x) => [x.id, x.unused])), [lowStock]);

  return (
    <div className="space-y-6" data-testid="admin-codes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Code Inventory</h1>
          <p className="text-sm text-slate-400 mt-1">Manage encrypted activation keys per product. Auto-assigned on paid orders.</p>
        </div>
        {lowStock.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
            <AlertTriangle className="w-4 h-4" /> {lowStock.length} product{lowStock.length > 1 ? "s" : ""} running low on codes
          </div>
        )}
      </div>

      {/* Low-stock alerts */}
      {lowStock.length > 0 && (
        <div className="rounded-2xl bg-[#171723] border border-amber-500/20 p-5">
          <h3 className="font-display font-semibold text-amber-300 text-sm mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Low stock alerts (&lt; 5 unused codes)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStock.map((p) => (
              <button key={p.id} onClick={() => setActiveProduct(p)} className="flex items-center gap-3 rounded-xl bg-amber-500/5 hover:bg-amber-500/10 p-3 border border-amber-500/15 text-left">
                <img src={p.cover_image} alt={p.title} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{p.title}</div>
                  <div className="text-xs text-amber-300">{p.unused} code{p.unused !== 1 ? "s" : ""} left</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="rounded-2xl bg-[#171723] border border-white/5 p-5">
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="w-full pl-10 pr-3 h-10 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="codes-search" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr>
              <th className="text-left px-2 py-2">Product</th>
              <th className="text-left px-2 py-2">Category</th>
              <th className="text-left px-2 py-2">Unused</th>
              <th className="text-left px-2 py-2">Used</th>
              <th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const unused = lowMap[p.id];
                return (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="px-2 py-3"><div className="flex items-center gap-3"><img src={p.cover_image} alt={p.title} className="w-9 h-9 rounded-lg object-cover" /><div className="font-medium text-white truncate max-w-[200px]">{p.title}</div></div></td>
                    <td className="px-2 py-3 text-slate-300">{p.category}</td>
                    <td className="px-2 py-3">{unused !== undefined ? <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${unused < 3 ? "bg-red-500/15 text-red-400" : unused < 5 ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{unused}</span> : <span className="text-slate-500 text-xs">—</span>}</td>
                    <td className="px-2 py-3 text-slate-400 text-xs">—</td>
                    <td className="px-2 py-3 text-right"><Button variant="outline" size="sm" onClick={() => setActiveProduct(p)} className="border-white/10 text-slate-200 hover:bg-white/5" data-testid={`manage-codes-${p.id}`}><Key className="w-3.5 h-3.5 mr-1.5" />Manage</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {activeProduct && <CodesDrawer product={activeProduct} onClose={() => setActiveProduct(null)} onChanged={reloadAll} />}
    </div>
  );
}

function CodesDrawer({ product, onClose, onChanged }) {
  const [data, setData] = useState({ items: [], stats: { total: 0, unused: 0, used: 0 } });
  const [loading, setLoading] = useState(true);
  const [bulk, setBulk] = useState("");
  const [adding, setAdding] = useState(false);
  const [revealing, setRevealing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/products/${product.id}/codes`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/admin/products/${product.id}/codes`);
        if (mounted) setData(r.data);
      } catch (e) { console.error(e); } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [product.id]);

  const addBulk = async () => {
    if (!bulk.trim()) return;
    setAdding(true);
    try {
      const { data: r } = await api.post(`/admin/products/${product.id}/codes`, { codes: bulk });
      toast.success(`Added ${r.added} code${r.added !== 1 ? "s" : ""}`);
      setBulk("");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setAdding(false); }
  };

  const reveal = async (code) => {
    if (revealing[code.id]?.code) {
      // toggle hide
      setRevealing({ ...revealing, [code.id]: { hidden: true } });
      return;
    }
    try {
      const { data: r } = await api.get(`/admin/products/${product.id}/codes/${code.id}/reveal`);
      setRevealing({ ...revealing, [code.id]: { code: r.code } });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyCode = async (code) => {
    try {
      const { data: r } = await api.get(`/admin/products/${product.id}/codes/${code.id}/reveal`);
      navigator.clipboard.writeText(r.code);
      toast.success("Copied");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (code) => {
    if (!window.confirm("Delete this code? (cannot be undone)")) return;
    await api.delete(`/admin/products/${product.id}/codes/${code.id}`);
    load();
    onChanged?.();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur p-4" onClick={onClose} data-testid="codes-drawer">
      <motion.div initial={{ scale: 0.96, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} className="w-full max-w-3xl rounded-3xl bg-[#171723] border border-white/10 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-white/5 flex items-center gap-4">
          <img src={product.cover_image} alt={product.title} className="w-12 h-12 rounded-xl object-cover" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-white truncate">{product.title}</h3>
            <p className="text-xs text-slate-400">Activation codes inventory</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400"><X className="w-5 h-5" /></button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 px-6 py-4 border-b border-white/5">
          <Stat label="Total" value={data.stats.total} />
          <Stat label="Unused" value={data.stats.unused} highlight={data.stats.unused < 5} />
          <Stat label="Used" value={data.stats.used} />
        </div>

        {/* Add bulk */}
        <div className="px-6 py-4 border-b border-white/5">
          <div className="text-xs text-slate-400 uppercase font-semibold mb-2">Add codes (one per line)</div>
          <textarea
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder="XXXX-XXXX-XXXX-XXXX&#10;YYYY-YYYY-YYYY-YYYY&#10;ZZZZ-ZZZZ-ZZZZ-ZZZZ"
            rows={4}
            className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white font-mono"
            data-testid="bulk-codes-input"
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-slate-500">Codes are encrypted (AES-128 Fernet) before storage.</p>
            <Button onClick={addBulk} disabled={adding || !bulk.trim()} className="bg-[#A855F7] hover:bg-[#9333EA]" data-testid="add-bulk-codes">
              {adding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Add codes
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? <div className="text-center py-8 text-slate-500">Loading…</div> : data.items.length === 0 ? <div className="text-center py-12"><Package className="w-10 h-10 text-slate-600 mx-auto mb-2" /><p className="text-sm text-slate-500">No codes yet — add your first batch above.</p></div> : (
            <div className="space-y-1.5">
              {data.items.map((c) => {
                const r = revealing[c.id];
                return (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/[0.07] transition-colors" data-testid={`code-row-${c.id}`}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.status === "unused" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{c.status}</span>
                    <span className="font-mono text-sm text-slate-200 flex-1 truncate">{r?.code && !r.hidden ? r.code : c.masked}</span>
                    <span className="text-xs text-slate-500 hidden sm:block">{c.used_at ? `used ${new Date(c.used_at).toLocaleDateString()}` : ""}</span>
                    <button onClick={() => reveal(c)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white" data-testid={`reveal-${c.id}`}><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => copyCode(c)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white" data-testid={`copy-${c.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    {c.status === "unused" && <button onClick={() => del(c)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400" data-testid={`del-code-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-xl bg-white/5 p-3 text-center">
      <div className="text-[10px] uppercase text-slate-500 tracking-wider font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${highlight ? "text-amber-400" : "text-white"}`}>{value}</div>
    </div>
  );
}
